// [VV5-TEARDOWN] lp-free-shipping flat-shipping A/B test (DEV-9829).
// vv5 lp-free-shipping experiment — variant rule (single source of truth).
//
// Client-safe: this module must NOT import `next/server` (unlike
// `../middleware/freeShipping.ts`), so it can be pulled into client bundles —
// the LP checkout (`@local/offer`) reads the cookie through here to decide
// whether to charge shipping and hide free-shipping messaging.
//
// Reuses the retired `vv5` cookie slot (old `lp-kit` kit / kit-wizard test,
// torn down 2026-06-16) with a fresh 30-day TTL. Old cookies may still hold
// `kit` / `kit-wizard`; `isValidVv5Variant` rejects them so stale browsers are
// re-bucketed into this two-way split.
//
// ─── TEARDOWN CHECKLIST (grep `VV5-TEARDOWN` across the repo) ───────────────
// When this test concludes, remove/revert every touchpoint below. If the flat
// fee WINS, promote it via real entries (one per FREE_SHIPPING_LP_CHANNELS
// slug) in `packages/cart/src/cart/SHIPPING_COSTS_CONFIG` (protected — ask an
// owner) instead of the cookie-gated overrides, then delete this experiment
// code. NOTE: the test arm charges GB £2.99, not the £7.95 storefront rate —
// see VV5_FLAT_SHIPPING_OVERRIDES. Promote 299 for `gb`, not 795.
//
// DELETE these files (entirely test-only):
//   packages/tracking/src/experiments/freeShipping.ts            (this file)
//   packages/tracking/src/middleware/freeShipping.ts
//   packages/tracking/src/middleware/getVv5.ts
//   packages/ui/src/components/Blocks/Highlight/HighlightNoShipping.tsx
//   packages/ui/src/components/Blocks/Highlight/Vv5ExposureBeacon.tsx
//   packages/offer/src/modules/common/Header/Vv5Topbar.tsx
//   packages/tracking/src/__tests__/freeShippingMiddleware.test.ts
//   packages/tracking/src/__tests__/freeShippingExperiment.test.ts
//   packages/payment/src/helpers/__tests__/flatShipping.test.ts
//   packages/tracking/src/experiments/checkoutExposure.ts    (vv5 is its only
//     consumer since the vv10 teardown — the whole file goes)
//   packages/tracking/src/__tests__/checkoutExposure.test.ts
//
// REVERT the marked `[VV5-TEARDOWN]` blocks in these files:
//   packages/tracking/src/middleware/index.ts        (wiring: header + cookie)
//   packages/tracking/src/middleware/url/const.ts     (vv5 param comment)
//   packages/payment/src/helpers/shippingRates.ts     (getFlatShippingCost +
//     VV5_FLAT_SHIPPING_OVERRIDES + `flatShipping` params)
//   packages/payment/src/modules/stripe/express.tsx   (flatShipping gate + args)
//   packages/payment/src/modules/paypal/usePayPalExpressPayment.ts
//   packages/payment/src/components/ShippingSelector.tsx
//   packages/payment/src/modules/stripe/useStripeCheckout.ts (order metadata tag)
//   packages/payment/src/modules/paypal/shared.ts            (order metadata tag)
//   packages/payment/src/helpers/getFreeOrderSessionBody.ts  (order metadata tag)
//   packages/offer/src/builder.tsx                    (dispatch + beacon + ContentIconsRow gate)
//   apps/store/src/app/[market]/(multi)/lp/[channel]/(home)/layout.tsx (passes freeShippingChannels)
//   packages/offer/src/modules/lp/LpHomeHeader.tsx   (mounts Vv5Topbar; keep `topbar: formattedMessage` and drop the prop)
//   packages/offer/src/modules/common/Header/types.ts (HeaderProps.data topbar widened to ReactNode)
//   packages/offer/src/modules/lp/Checkout/CheckoutOfferBuilder.tsx (fee + messaging + checkout topbar copy)
//   packages/offer/src/modules/lp/Checkout/components/QuantitySelector/QuantitySelector.tsx (hasFreeShipping)
//   packages/ui/src/components/Blocks/Highlight/index.tsx           (hideShippingInformation)
//   packages/ui/src/components/Blocks/ContentIconsRow/index.tsx     (hideFreeShipping)
//   packages/offer/src/modules/common/StaticPages/CookiePolicy/master.tsx (vv5 cookie row)
//   packages/devtools/src/panel/components/ABTestingTab.tsx (vv5 EXPERIMENTS entry)
//   apps/store/src/tests/policy-pages.spec.ts         (vv5 cookie-policy assertion)
//
// EXTERNAL: archive the `lp-free-shipping` PostHog feature flag.
// ───────────────────────────────────────────────────────────────────────────

/** Cookie / query-override slot name for the vv5 experiment. */
export const VV5_COOKIE = "vv5"

/**
 * PostHog feature-flag key for this experiment (single source of truth).
 * The `control` / `test` cookie buckets map 1:1 to this flag's variant keys.
 * Exposure is fired client-side on the sunbeater LP highlight mount — see
 * `packages/ui/src/components/Blocks/Highlight/Vv5ExposureBeacon.tsx` — and,
 * ordered ahead of the checkout `$pageview`, by
 * `packages/tracking/src/experiments/checkoutExposure.ts` (DEV-9883).
 */
export const VV5_FEATURE_FLAG = "lp-free-shipping"

/**
 * The landing-page channels this experiment runs on. Their ainchor sub-pages
 * (`/lp/{channel}/{ainchor}`) are in scope too — they render the same blocks
 * and funnel into the same checkout.
 *
 * `sunbeater-hoodie` since DEV-9829; `rancher-shirt` and
 * `backpack-hoodie-women` added by DEV-10024, `leggings` by DEV-10360 — each
 * increments the same test rather than spinning up a second one, so
 * already-bucketed visitors keep their arm on the new pages.
 */
export const FREE_SHIPPING_LP_CHANNELS = [
  "sunbeater-hoodie",
  "rancher-shirt",
  "backpack-hoodie-women",
  "leggings",
] as const

/**
 * Whether a channel slug is enrolled in the experiment. Exact membership, so
 * the unenrolled `backpack-hoodie` never matches `backpack-hoodie-women`.
 */
export const isFreeShippingLpChannel = (channel?: string | null): boolean =>
  !!channel &&
  (FREE_SHIPPING_LP_CHANNELS as readonly string[]).includes(channel)

/**
 * Valid variant values for the vv5 lp-free-shipping experiment. Even split
 * (50 / 50):
 *   - control → free shipping everywhere, all free-shipping messaging shown
 *   - test    → a flat shipping fee charged on every order (the store's local
 *               rate, except where VV5_FLAT_SHIPPING_OVERRIDES lowers it — GB
 *               is £2.99), all free-shipping messaging removed
 */
export type FreeShippingVariant = "control" | "test"
const VALID_VARIANTS: FreeShippingVariant[] = ["control", "test"]

/**
 * Whether a raw cookie / override string is a valid current variant. Rejects
 * the stale `kit` / `kit-wizard` values left by the old lp-kit test so those
 * browsers fall through to a fresh assignment.
 */
export const isValidVv5Variant = (
  value: string | null | undefined
): value is FreeShippingVariant =>
  value != null && VALID_VARIANTS.includes(value as FreeShippingVariant)

/**
 * Whether the visitor should be charged the flat shipping fee (and have
 * free-shipping messaging removed). Only the `test` arm charges; a missing /
 * invalid / control value keeps free shipping (safe default — matches the
 * current experience).
 */
export const shouldChargeShipping = (vv5?: string): boolean => vv5 === "test"

/**
 * Test-arm promo-banner copy — the free-shipping wording removed. Used for BOTH
 * the LP header topbar and the checkout marquee banner (single source so the two
 * surfaces never drift). US keeps the delivery + returns line minus "& FREE";
 * other markets use the returns-only line with a siren. The control arm keeps
 * the CMS banner untouched, so this is only rendered when `vv5 === "test"`.
 */
export const freeShippingTestBanner = (
  market?: string | null,
  discount = 60
): string =>
  market?.toLowerCase() === "us"
    ? `${discount}% OFF + FAST USA DELIVERY + 60-DAY RETURNS!`
    : `🚨 ${discount}% OFF + 60-DAY RETURNS!`

/**
 * Whether a pathname is one of the enrolled free-shipping LPs or anything
 * beneath it (`/{market}/lp/{channel}`, its ainchor sub-pages, its checkout) —
 * but NOT a different LP that merely starts with an enrolled slug (e.g.
 * `…/sunbeater-hoodie-sale`), because the slug must terminate at a `/` or the
 * end of the path. That terminator is also what keeps the unenrolled
 * `backpack-hoodie` out while `backpack-hoodie-women` is in.
 *
 * Anchored at the market segment, mirroring `parseFreeShippingPath` in the
 * middleware: a persisted `vv5` cookie must not leak the fee / order tag onto
 * a look-alike route, and an unanchored match would accept nested shapes the
 * middleware rejects (`/us/other/lp/rancher-shirt`) plus the market-less legacy
 * form, which `(legacy)/lp/[slug]` redirects away before anything renders.
 */
const FREE_SHIPPING_LP_PATH_RE = new RegExp(
  `^/[^/]+/lp/(?:${FREE_SHIPPING_LP_CHANNELS.join("|")})(?:/|$)`
)
export const isFreeShippingLpPath = (pathname?: string | null): boolean =>
  !!pathname && FREE_SHIPPING_LP_PATH_RE.test(pathname)

/**
 * Whether the flat shipping fee should be charged for a client-side flow
 * (main checkout + express/wallet). True only on the `test` arm AND when we're
 * on an enrolled LP — detected by the cart channel slug OR the URL pathname,
 * so express can't silently fall back to free if the `channel` prop ever
 * diverges from the URL slug.
 */
export const isFreeShippingTestActive = ({
  channel,
  pathname,
  vv5,
}: {
  channel?: string
  pathname?: string | null
  vv5?: string
}): boolean =>
  shouldChargeShipping(vv5) &&
  (isFreeShippingLpChannel(channel) || isFreeShippingLpPath(pathname))

/**
 * Order-payload tag for server-side per-arm revenue analysis (survives
 * ad-blockers, unlike client PostHog). Returns `{ experiment_lp_free_shipping:
 * <variant> }` only for orders placed on an enrolled LP checkout with a valid
 * variant, else `{}` so unrelated orders aren't tagged. Pure (no cookie/window
 * access) so it stays edge-safe — callers pass the pathname + cookie value.
 */
export const freeShippingOrderTag = (
  pathname: string | null | undefined,
  vv5: string | undefined
):
  | { experiment_lp_free_shipping: FreeShippingVariant }
  | Record<string, never> =>
  isFreeShippingLpPath(pathname) && (vv5 === "control" || vv5 === "test")
    ? { experiment_lp_free_shipping: vv5 }
    : {}
